import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck, ShieldAlert, Printer } from 'lucide-react';
import { Button } from '../ui/button';
import { BaaDocument } from './BaaDocument';
import { BaaSignForm } from './BaaSignForm';
import { SignedRecord } from './BaaSignatures';
import { baaApi, BAA_SIGNED_EVENT, formatSignedAt } from './baaApi';

const StaffNotice = () => (
  <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900" data-testid="baa-staff-notice">
    Only your practice administrator can sign the Business Associate Agreement. Until it's signed, patient documents and messages can't be sent.
  </div>
);

const Header = ({ a }) => (
  <div className="flex flex-wrap items-center gap-4 px-6 py-5 bg-[#0b2545] text-white">
    {a ? <ShieldCheck className="w-8 h-8 text-emerald-300" /> : <ShieldAlert className="w-8 h-8 text-amber-300" />}
    <div className="min-w-0 flex-1">
      <h2 className="font-bold text-lg">HIPAA Business Associate Agreement</h2>
      <p className="text-sm text-slate-300" data-testid="baa-settings-status">
        {a ? `Signed by ${a.representativeName} on ${formatSignedAt(a.signedAt)} · Version ${a.baaVersion}`
          : 'Not signed yet. Sending patient documents and messages is paused until it is signed.'}
      </p>
    </div>
    {a && (
      <Button variant="ghost" size="sm" onClick={() => window.print()} data-testid="baa-print-btn" className="text-white hover:bg-white/10 hover:text-white">
        <Printer className="w-4 h-4 mr-1" /> Print
      </Button>
    )}
  </div>
);

export const BaaSettingsCard = () => {
  const [status, setStatus] = useState(null);
  const [doc, setDoc] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    baaApi.status().then(setStatus).catch(() => {});
    baaApi.document().then(setDoc).catch(() => {});
  }, []);

  useEffect(() => {
    if (status && window.location.hash === '#baa') setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  }, [status]);

  if (!status) return null;
  const a = status.agreement;
  const onSigned = (agreement) => {
    setStatus({ ...status, signed: true, agreement });
    window.dispatchEvent(new Event(BAA_SIGNED_EVENT));
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="baa" ref={ref} data-testid="baa-settings-card"
      className={`rounded-2xl border bg-white overflow-hidden scroll-mt-24 baa-print ${a ? 'border-slate-200' : 'border-amber-300 ring-4 ring-amber-100'}`}>
      <Header a={a} />
      <div className="grid lg:grid-cols-[1.15fr_1fr]">
        <div className="max-h-[640px] overflow-y-auto px-6 py-6 lg:px-8 border-r" data-testid="baa-document-scroll">
          {a?.documentText || doc?.text ? <BaaDocument text={a?.documentText || doc.text} /> : <p className="text-sm text-slate-400">Loading agreement…</p>}
        </div>
        <div key={a ? "signed" : "form"} className="max-h-[640px] overflow-y-auto px-6 py-6 bg-[#f6f9fc]">
          {a ? <SignedRecord a={a} /> : status.canSign ? <BaaSignForm prefill={status.prefill} onSigned={onSigned} /> : <StaffNotice />}
        </div>
      </div>
    </section>
  );
};
